import { FC, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';

interface Column {
  label: string;
  field: string;
  className?: string;
  format?: string;
  helpField?: string;
}

interface SimpleTableProps {
  data?: any[];
  columns: Column[];
  showLoader?: boolean;
  onRowClick?: (row: any, index: number) => void;
  activeProposalId?: number;
}

const SimpleTable: FC<SimpleTableProps> = ({
  columns,
  showLoader = false,
  onRowClick,
  activeProposalId,
  data,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columnHelper = createColumnHelper<any>();
  const { t } = useTranslation();

  const tableColumns = useMemo(() => {
    return columns.map((column) => {
      return columnHelper.accessor(column.field as any, {
        id: column.field,
        header: ({ column: tableColumn }) => (
          <div className="d-flex flex-between">
            <span style={{ marginInlineEnd: 5 }}>{column.label}</span>
            {tableColumn.getCanSort() && (
              <span className="cur-poi ml-2" onClick={tableColumn.getToggleSortingHandler()}>
                {tableColumn.getIsSorted() === 'asc' && (
                  <FontAwesomeIcon className="text-primary" icon={faSortUp} />
                )}
                {tableColumn.getIsSorted() === 'desc' && (
                  <FontAwesomeIcon className="text-primary" icon={faSortDown} />
                )}
                {!tableColumn.getIsSorted() && (
                  <FontAwesomeIcon icon={faSort} className="text-light" />
                )}
              </span>
            )}
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          const value = row[column.field];

          if (column.format === 'html' && column.helpField) {
            return <div dangerouslySetInnerHTML={{ __html: row[column.helpField] }} />;
          }

          if (column.format === 'jsx' && column.helpField) {
            return row[column.helpField];
          }

          return <span>{value}</span>;
        },
        enableSorting: true,
      });
    });
  }, [columns, columnHelper]);

  const table = useReactTable({
    data: data || [],
    columns: tableColumns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="table-default table-high table-bordered-lines">
      {!!data?.length && (
        <table className="table-mobile">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={columns.find((col) => col.field === header.id)?.className || ''}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original, index)}
                className={
                  (onRowClick ? 'cur-poi' : '') +
                  (activeProposalId === row.original.id ? ' active' : '')
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={columns.find((col) => col.field === cell.column.id)?.className}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!data && showLoader && (
        <table className="table-mobile">
          <tbody>
            {Array.from({ length: 20 }).map((_, index) => (
              <tr key={index}>
                {columns.map((column, subIndex) => (
                  <td key={subIndex} className={column.className}>
                    <Skeleton height={20} width="80%" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!data?.length && (
        <div className="text-center flex-center mt-20 mb-20">
          {t('search.no_results')}
        </div>
      )}
    </div>
  );
};

export default SimpleTable;
